import { useEffect, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

type Tone = "destructive" | "primary" | "success";

const TONE: Record<Tone, { halo: string; chip: string }> = {
  destructive: { halo: "bg-destructive/15", chip: "bg-destructive/15 text-destructive" },
  primary: { halo: "bg-primary/15", chip: "bg-primary/15 text-primary" },
  success: { halo: "bg-success/15", chip: "bg-success/15 text-success" },
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason?: string) => void;
  icon: LucideIcon;
  tone?: Tone;
  title: string;
  description?: string;
  confirmLabel?: string;
  confirmIcon?: ReactNode;
  cancelLabel?: string;
  loading?: boolean;
  /** Show a reason textarea; the value is passed to onConfirm. */
  withReason?: boolean;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
}

/**
 * Sweet-alert style confirmation, matching the logout dialog: frosted blur
 * backdrop, spring-in card, a tone-tinted halo icon, and (optionally) a reason
 * textarea. Used for every destructive / state-changing admin action so the
 * portal never falls back to a native browser prompt.
 */
export function ConfirmDialog({
  open, onOpenChange, onConfirm, icon: Icon, tone = "primary",
  title, description, confirmLabel = "Confirm", confirmIcon, cancelLabel = "Cancel",
  loading = false, withReason = false, reasonLabel, reasonPlaceholder, reasonRequired = false,
}: Props) {
  const [reason, setReason] = useState("");
  const t = TONE[tone];

  // Reset the field each time the dialog opens.
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const canConfirm = !loading && (!withReason || !reasonRequired || reason.trim().length > 0);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="fixed inset-0 z-50 bg-black/45 backdrop-blur-md"
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 6 }}
                transition={{ type: "spring", stiffness: 320, damping: 26, mass: 0.7 }}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-border bg-card p-6 shadow-2xl"
              >
                <div className="relative mx-auto flex size-16 items-center justify-center">
                  <span className={`absolute inset-0 rounded-full blur-md ${t.halo}`} />
                  <span className={`relative flex size-14 items-center justify-center rounded-full ${t.chip}`}>
                    <Icon className="size-7" />
                  </span>
                </div>

                <DialogPrimitive.Title asChild>
                  <h2 className="mt-4 text-center text-lg font-bold tracking-tight">{title}</h2>
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description asChild>
                    <p className="mt-1.5 text-center text-sm text-muted-foreground">{description}</p>
                  </DialogPrimitive.Description>
                )}

                {withReason && (
                  <div className="mt-4">
                    {reasonLabel && (
                      <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                        {reasonLabel}
                      </label>
                    )}
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder={reasonPlaceholder}
                      rows={3}
                      className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                )}

                <div className="mt-6 flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
                    {cancelLabel}
                  </Button>
                  <Button
                    variant={tone === "destructive" ? "destructive" : "default"}
                    className="flex-1"
                    disabled={!canConfirm}
                    onClick={() => onConfirm(withReason ? reason.trim() || undefined : undefined)}
                  >
                    {confirmIcon}
                    {loading ? "Working…" : confirmLabel}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
